<template>
  <div v-on-clickaway="onBlur" :class="wrapperClass">
    <a-icon
      :is-button="true"
      :icon="$options.consts.ICON.MAGNIFIER_3"
      :no-hover="true"
      :width="16"
      :height="16"
      class="quicksearch__search-icon"
      @click="settings.viewAllResultsClickHandler"
    />
    <a-text-input
      ref="search"
      v-model="searchPhrase"
      :placeholder="placeholderValue"
      :max-length="$options.consts.QUICK_SEARCH_MAX_LENGTH_LIMITATION"
      :input-style="$options.consts.INPUT_STYLE.STYLE_6"
      :hide-label="hideLabel"
      @input="onSearch"
      @focus="onFocus"
      @search="settings.viewAllResultsClickHandler"
      @keyup.enter="settings.viewAllResultsClickHandler"
    />
    <quick-search-results
      v-if="isDropdownVisible"
      :items="items"
      :settings="settings"
      :in-progress="inProgress"
      :hide-result-button="hideResultButton"
      @blur="onBlur"
      @hook:mounted="isFocused = true"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { directive as onClickaway } from 'vue-clickaway'
import debounce from 'debounce'

import { propValidator, PROP_TYPES } from '@/utils/validators'
import mixins from '@/utils/mixins'
import { ROUTE, ROUTE_NAME } from 'enums/routes'
import { checkIfMigratedTag } from 'enums/tags'
import QuickSearchResults from 'shared/AQuickSearch/QuickSearchResults'
import { SEARCH_TRACKING_EVENT_TYPE } from 'enums/track-search'
import { MIN_CHAR_COUNT_FOR_TRACKING_SEARCH } from '@/utils/mixins/track-search'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { QUICK_SEARCH_MAX_LENGTH_LIMITATION } from 'enums/quicksearch'
import AIcon, { ICON } from 'shared/AIcon'
import { INPUT_STYLE } from 'shared/ATextInput'

export const SEARCH_MIN_CHAR_COUNT = 2

export const QUICKSEARCH_TYPE = {
  COMPANIES: 'COMPANIES',
  TERMS: 'TERMS',
  TAGS: 'TAGS',
  FL_DIR_SEARCH_HISTORY: 'FL_DIR_SEARCH_HISTORY'
}

const DEFAULT_SEARCH_HISTORY_PAGE = 0
const DEFAULT_SEARCH_HISTORY_TAKE = 10

export default {
  name: 'AQuickSearch',
  components: { QuickSearchResults, AIcon },
  mixins: [mixins.urlGenerators, mixins.trackSearch, hydrationHelpers],
  directives: { onClickaway },
  model: {
    event: 'on-search'
  },
  props: {
    type: propValidator([PROP_TYPES.STRING], true, null, value =>
      Object.values(QUICKSEARCH_TYPE).includes(value)
    ),
    searchRequestParams: propValidator([PROP_TYPES.OBJECT], false, () => {}),
    value: propValidator([PROP_TYPES.STRING], false, ''),
    useTrackSearch: propValidator([PROP_TYPES.BOOLEAN], false, true),
    hideLabel: propValidator([PROP_TYPES.BOOLEAN], false, true),
    hideResultButton: propValidator([PROP_TYPES.BOOLEAN], false, false),
    placeholder: propValidator([PROP_TYPES.STRING], false)
  },
  consts: {
    searchTrackingEventByType: {
      [QUICKSEARCH_TYPE.TERMS]:
        SEARCH_TRACKING_EVENT_TYPE.IL_TERMS_QUICK_SEARCH,
      [QUICKSEARCH_TYPE.TAGS]: SEARCH_TRACKING_EVENT_TYPE.IL_TAGS_QUICK_SEARCH,
      [QUICKSEARCH_TYPE.COMPANIES]:
        SEARCH_TRACKING_EVENT_TYPE.IL_COMPANIES_QUICK_SEARCH
    },
    QUICK_SEARCH_MAX_LENGTH_LIMITATION,
    resetComponentDataFields: ['items', 'inProgress', 'isFocused'],
    ICON,
    INPUT_STYLE
  },
  data() {
    return {
      items: [],
      inProgress: false,
      isFocused: false,
      searchPhrase: ''
    }
  },
  computed: {
    currentTrackingEventType() {
      return this.$options.consts.searchTrackingEventByType[this.type]
    },
    isDropdownVisible() {
      return (
        (this.isFocused || this.$refs?.search?.isFocused) &&
        this.searchPhrase &&
        this.searchPhrase.length >= SEARCH_MIN_CHAR_COUNT &&
        this.$helper.isTrimmedStringNotEmpty(this.searchPhrase)
      )
    },
    searchHistoryRequestParams() {
      return {
        queryParams: {
          page: DEFAULT_SEARCH_HISTORY_PAGE,
          take: DEFAULT_SEARCH_HISTORY_TAKE,
          searchPhrase: this.searchPhrase
        }
      }
    },
    settings() {
      const settings = {
        [QUICKSEARCH_TYPE.TERMS]: {
          handler: this.requestTermsQuicksearch,
          redirectRoute: ROUTE.TERMS_SEARCH_RESULTS,
          viewAllResultsClickHandler: this.redirectToSearchPage,
          generateItemUrl: this.generateTermRoute,
          isItemUrlWithTrailingSlash: () => true,
          placeholder: 'Search any Glossary Term'
        },
        [QUICKSEARCH_TYPE.TAGS]: {
          handler: this.requestTagsQuicksearch,
          redirectRoute: ROUTE.TAG_SEARCH_RESULTS,
          viewAllResultsClickHandler: this.redirectToSearchPage,
          generateItemUrl: this.generateTagPath,
          isItemUrlWithTrailingSlash: item => !checkIfMigratedTag(item.Slug),
          placeholder: 'Search for a tag'
        },
        [QUICKSEARCH_TYPE.COMPANIES]: {
          handler: this.requestCompaniesQuicksearch,
          handlerParams: this.companiesQuicksearchRequestParams,
          redirectRouteName: ROUTE_NAME.CATEGORY,
          viewAllResultsClickHandler: this.viewAllCompaniesClickHandler,
          generateItemUrl: this.generateCompanyPath,
          isItemUrlWithTrailingSlash: () => true,
          placeholder: this.$_hydrationHelpers_mobileOrDesktopValue(
            '',
            'Search Brokers'
          )
        },
        [QUICKSEARCH_TYPE.FL_DIR_SEARCH_HISTORY]: {
          handler: this.requestSearchHistoryQuicksearch,
          handlerParams: this.searchHistoryRequestParams,
          viewAllResultsClickHandler: this.redirectToSearchPage,
          generateItemUrl: this.generateFlDirSearchResultUrl,
          isItemUrlWithTrailingSlash: () => false,
          placeholder: 'Search my saved history'
        }
      }

      return settings[this.type]
    },
    companiesQuicksearchRequestParams() {
      return {
        ...this.searchRequestParams,
        searchPhrase: this.searchPhrase
      }
    },
    wrapperClass() {
      return ['quicksearch', { 'quicksearch--focused': this.isFocused }]
    },
    placeholderValue() {
      return this.placeholder || this.settings.placeholder
    },
    searchResultQueryParam() {
      return this.$route.query.searchPhrase
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        const initialValue = newVal || ''
        if (initialValue !== this.searchPhrase) {
          this.searchPhrase = initialValue
          this.requestQuicksearch(initialValue)
        }
      }
    },
    searchPhrase: function() {
      if (
        this.useTrackSearch &&
        this.searchPhrase?.length >= MIN_CHAR_COUNT_FOR_TRACKING_SEARCH
      ) {
        this.$_trackSearch_debouncedTrackSearch(
          this.searchPhrase,
          this.currentTrackingEventType
        )
      }
    }
  },
  methods: {
    ...mapActions({
      requestTermsQuicksearch: 'globalSearch/requestTermsQuicksearch',
      requestTagsQuicksearch: 'globalSearch/requestTagsQuicksearch',
      requestCompaniesQuicksearch: 'globalSearch/requestCompaniesQuicksearch',
      concurrentRouterPush: 'router/concurrentRouterPush',
      requestSearchHistoryQuicksearch:
        'personal-area/requestSearchHistoryQuicksearch'
    }),
    onFocus() {
      this.isFocused = true
      this.inProgress = true
      this.requestQuicksearch(this.searchPhrase)
    },
    onBlur() {
      this.isFocused = false
    },
    onSearch(searchPhrase) {
      this.searchPhrase = searchPhrase
      this.$emit('on-search', searchPhrase)
      this.inProgress = true
      this.debouncedSearch(searchPhrase)
    },
    debouncedSearch: debounce(function(searchPhrase) {
      this.requestQuicksearch(searchPhrase)
    }, 200),
    async requestQuicksearch(searchPhrase) {
      if (
        !searchPhrase ||
        searchPhrase.length < SEARCH_MIN_CHAR_COUNT ||
        !this.$helper.isTrimmedStringNotEmpty(searchPhrase)
      )
        return

      try {
        const params = this.settings.handlerParams || { searchPhrase }
        const { Items } = await this.settings.handler(params)
        this.items = Items
      } catch (err) {
        this.$errorHandler(err, this)
      } finally {
        this.inProgress = false
      }
    },
    redirectToSearchPage() {
      if (this.searchPhrase.length < SEARCH_MIN_CHAR_COUNT) return

      const payload = {
        router: this.$router,
        to: {
          ...(this.settings.redirectRoute
            ? { path: this.settings.redirectRoute }
            : { name: this.settings.redirectRouteName }),
          query: {
            searchPhrase: this.searchPhrase
          }
        }
      }
      this.concurrentRouterPush(payload)
      this.$helper.resetComponentData(
        this,
        this.$options.consts.resetComponentDataFields
      )
      this.$helper.blurActiveElement()
    },
    viewAllCompaniesClickHandler() {
      this.isFocused = false
      this.$emit('view-all-results')
    }
  },
  mounted() {
    this.searchPhrase = this.searchResultQueryParam
  }
}
</script>

<style lang="scss" scoped>
.quicksearch {
  position: relative;

  /deep/ .input__wrapper.input_style-6 {
    .input__placeholder {
      padding-left: 32px;
    }

    .input__input {
      width: 100%;
      max-height: 100%;
      padding: 12px 12px 12px 44px;
      font-size: 16px;
      line-height: 22px;
      color: $c--black;
    }
  }
}

.quicksearch__search-icon {
  position: absolute;
  left: 12px;
  top: 15px;
  z-index: 1;
  cursor: pointer;
  fill: $c--main;
}

/deep/ .input__component-wrapper {
  margin-bottom: 32px;
}
</style>
